<template>
    <div>
        <div v-if="cartIsEmpty" class="py-36 text-gray-700 text-center">
            <p>Your cart is empty.</p>
            <div class="mt-6">
                <a
                    role="button"
                    class="inline-flex justify-center px-6 py-3 my-2 shadow-sm text-base font-medium rounded-md text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                    href="/product-categories/all-products"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    Browse Our Products
                </a>
            </div>
        </div>

        <div v-else class="mx-6 sm:mx-12">
            <form
                class="flex flex-col"
                ref="form"
                action="/checkout"
                method="POST"
                @submit.prevent="onSubmit()"
            >
                <input type="hidden" name="_token" :value="csrf" />
                <input type="hidden" name="timezone" :value="timezone" />
                <input
                    v-if="state.shippingTimeId"
                    type="hidden"
                    name="shipping_option"
                    :value="state.shippingTimeId"
                />
                <input type="hidden" name="fingerprint" :value="fingerprint" />

                <div class="lg:grid lg:grid-cols-3">
                    <div class="lg:col-span-2">
                        <div
                            class="my-2 flex items-center justify-center sm:justify-start"
                        >
                            <p class="text-gray-500">
                                Already have an account?
                            </p>

                            <a
                                data-test="login"
                                role="button"
                                class="inline-flex justify-center items-center px-6 ml-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none shrink-0"
                                href="/login"
                            >
                                <LoginIcon class="h-5 w-5 mr-2" />
                                Log In
                            </a>
                        </div>

                        <GuestCheckoutContactInformationForm
                            :errors="formErrors"
                            :old="old"
                        />

                        <GuestCheckoutShippingAddressForm
                            :countries="countries"
                            :errors="formErrors"
                            :initialCountry="initialCountry"
                            :initialStates="initialStates"
                            :old="old"
                        />

                        <GuestCheckoutPaymentMethodForm
                            @paymentMethodEntered="onPaymentMethodEntered"
                            @cardDataTokenized="onCardDataTokenized"
                            @cardDataTokenizationFailed="
                                onCardDataTokenizationFailed
                            "
                            @payWithUpdated="(data) => (payWith = data)"
                            :amount="state.cartTotals.total"
                            :countries="countries"
                            :errors="errors"
                            :formErrors="formErrors"
                            :initialCountry="initialCountry"
                            :initialStates="initialStates"
                            :old="old"
                            :paymentGateway="paymentGateway"
                            :paypalToken="state.paypalToken"
                        />

                        <div id="cart-items">
                            <div
                                :class="[
                                    hasError(formErrors, 'cart')
                                        ? 'border-red-600'
                                        : 'border-gray-200',
                                    'bg-white shadow rounded-lg border',
                                ]"
                            >
                                <div
                                    :class="[
                                        hasError(formErrors, 'cart')
                                            ? 'bg-red-500 text-white border-red-600'
                                            : 'bg-gray-100 text-gray-800 border-gray-200',
                                        'flex items-center justify-between px-4 py-5 sm:px-6 border-b rounded-t-lg',
                                    ]"
                                >
                                    <div class="flex items-center">
                                        <ViewListIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        <h2 class="font-bold">Order Summary</h2>
                                    </div>

                                    <h3
                                        class="hidden sm:inline-block text-right font-semibold text-sm"
                                    >
                                        Price
                                    </h3>
                                </div>

                                <div class="divide-y divide-gray-200">
                                    <div
                                        :id="`cart-item-${cartItem.id}`"
                                        v-for="cartItem in state.cart
                                            .cart_items"
                                        :key="cartItem.id"
                                    >
                                        <div class="px-4 py-5 sm:p-6 flex">
                                            <CartItemImage
                                                :cartItem="cartItem"
                                                :linkToProductPage="false"
                                            />

                                            <div
                                                class="ml-3 w-full sm:grid sm:grid-cols-5"
                                            >
                                                <div class="col-span-4">
                                                    <p
                                                        class="font-semibold px-3 pb-1 text-gray-700"
                                                    >
                                                        {{
                                                            cartItem
                                                                .product_option
                                                                .product.name
                                                        }}
                                                        -
                                                        {{
                                                            cartItem
                                                                .product_option
                                                                .product
                                                                .description
                                                        }}
                                                        ({{
                                                            cartItem
                                                                .product_option
                                                                .description
                                                        }})
                                                    </p>

                                                    <p
                                                        class="sm:hidden px-3 py-2 text-gray-700 text-sm"
                                                    >
                                                        <span
                                                            class="font-semibold"
                                                            >Price:</span
                                                        >
                                                        ${{
                                                            dollarsForDisplay(
                                                                cartItem
                                                                    .product_option
                                                                    .price,
                                                            )
                                                        }}
                                                    </p>

                                                    <div
                                                        class="px-3 py-2 text-gray-700 text-sm sm:text-base flex items-center"
                                                    >
                                                        <span
                                                            class="font-semibold mr-2"
                                                            >Qty:</span
                                                        >
                                                        <CartItemQuantityButtons
                                                            :cartItem="cartItem"
                                                            :shippingTimeId="
                                                                state.shippingTimeId
                                                            "
                                                        />
                                                    </div>

                                                    <p
                                                        class="px-3 py-2 text-sm text-red-600"
                                                        v-if="
                                                            errors[
                                                                cartItem.id
                                                            ] &&
                                                            errors[cartItem.id]
                                                                .quantity
                                                        "
                                                    >
                                                        {{
                                                            errors[cartItem.id]
                                                                .quantity
                                                        }}
                                                    </p>

                                                    <CartItemSubscriptionOptionButtons
                                                        v-if="
                                                            cartItem.subscription_option_id
                                                        "
                                                        class="px-3 py-2 text-gray-700 text-sm sm:text-base flex items-center"
                                                        :cartItem="cartItem"
                                                        :shippingTimeId="
                                                            state.shippingTimeId
                                                        "
                                                    />

                                                    <p
                                                        class="px-3 py-2 text-sm text-red-600"
                                                        v-if="
                                                            errors[
                                                                cartItem.id
                                                            ] &&
                                                            errors[cartItem.id]
                                                                .subscription_option_id
                                                        "
                                                    >
                                                        {{
                                                            errors[cartItem.id]
                                                                .subscription_option_id
                                                        }}
                                                    </p>

                                                    <CartItemDiscounts
                                                        :cartItem="cartItem"
                                                        :discounts="
                                                            state.discounts
                                                        "
                                                    />

                                                    <CartItemTips
                                                        :cartItem="cartItem"
                                                        :shippingTimeId="
                                                            state.shippingTimeId
                                                        "
                                                        :tips="state.tips"
                                                    />
                                                </div>

                                                <div
                                                    class="hidden sm:flex sm:flex-col sm:justify-between sm:items-end"
                                                >
                                                    <span>
                                                        ${{
                                                            dollarsForDisplay(
                                                                cartItem
                                                                    .product_option
                                                                    .price,
                                                            )
                                                        }}
                                                    </span>

                                                    <DeleteCartItemButton
                                                        :cartItem="cartItem"
                                                        :shippingTimeId="
                                                            state.shippingTimeId
                                                        "
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div class="md:sticky md:top-2 lg:pl-6 flex flex-col">
                            <div class="flex flex-col">
                                <div class="order-last lg:order-first">
                                    <button
                                        name="place_order"
                                        type="submit"
                                        class="w-full inline-flex justify-center px-6 py-5 lg:py-3 my-2 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-6 w-6 mr-2"
                                        />
                                        <LockClosedIcon
                                            v-else
                                            class="h-6 w-6 mr-2"
                                            aria-hidden="true"
                                        />
                                        <span>Place Your Order</span>
                                    </button>
                                </div>

                                <div
                                    class="order-first lg:order-last bg-white shadow border border-gray-200 rounded-lg mb-4 lg:mt-4"
                                >
                                    <div id="cart-totals" v-if="!cartIsEmpty">
                                        <div class="w-full px-4 py-4">
                                            <div
                                                class="mb-4 flex items-baseline justify-between"
                                            >
                                                <dt
                                                    class="font-semibold text-gray-500"
                                                >
                                                    Subtotal:
                                                </dt>
                                                <dd class="mt-1 text-gray-800">
                                                    ${{
                                                        dollarsForDisplay(
                                                            state.cartTotals
                                                                .subtotal,
                                                        )
                                                    }}
                                                </dd>
                                            </div>

                                            <div
                                                v-if="
                                                    state.cartTotals.discounts >
                                                    0
                                                "
                                                class="mb-4 flex items-baseline justify-between"
                                            >
                                                <dt
                                                    class="font-semibold text-gray-500"
                                                >
                                                    Discounts:
                                                </dt>
                                                <dd class="mt-1 text-gray-800">
                                                    - ${{
                                                        dollarsForDisplay(
                                                            state.cartTotals
                                                                .discounts,
                                                        )
                                                    }}
                                                </dd>
                                            </div>

                                            <CheckoutShippingOptionButtons
                                                class="py-1"
                                                @shippingTimeUpdated="
                                                    (shippingTimeId) =>
                                                        (state.shippingTimeId =
                                                            shippingTimeId)
                                                "
                                                :country="state.country"
                                                :region="state.region"
                                                :shippingTimeId="
                                                    state.shippingTimeId
                                                "
                                                :shippingOptions="
                                                    state.shippingOptions
                                                "
                                            />

                                            <p
                                                class="px-3 py-1 text-sm text-red-600"
                                                v-if="
                                                    state.shippingTimeId &&
                                                    (errors.shipping_option ||
                                                        hasError(
                                                            formErrors,
                                                            'shipping_option',
                                                        ))
                                                "
                                            >
                                                {{
                                                    errors.shipping_option
                                                        ? errors.shipping_option
                                                        : error(
                                                              formErrors,
                                                              "shipping_option",
                                                          )
                                                }}
                                            </p>

                                            <div
                                                v-if="state.cartTotals.tax > 0"
                                                class="mt-2 mb-4 flex items-baseline justify-between"
                                            >
                                                <dt
                                                    class="font-semibold text-gray-500"
                                                >
                                                    Tax
                                                    <Tooltip
                                                        :message="
                                                            state.country
                                                                .code === 'US'
                                                                ? 'We collect and remit sales tax according to the rates set by your state and local government.'
                                                                : 'We collect taxes and pay them directly to your local government to prevent unexpected fees or delays.'
                                                        "
                                                    />
                                                    :
                                                </dt>
                                                <dd class="mt-1 text-gray-800">
                                                    ${{
                                                        dollarsForDisplay(
                                                            state.cartTotals
                                                                .tax,
                                                        )
                                                    }}
                                                </dd>
                                            </div>

                                            <div class="mt-4 mb-2">
                                                <div
                                                    class="flex items-baseline justify-between"
                                                >
                                                    <dt
                                                        class="font-bold text-gray-500 text-xl"
                                                    >
                                                        Total ({{
                                                            `${state.cartQuantity} ${state.cartQuantity === 1 ? "Item" : "Items"}`
                                                        }}):
                                                    </dt>
                                                    <dd
                                                        class="mt-1 font-semibold text-xl text-gray-800"
                                                    >
                                                        ${{
                                                            dollarsForDisplay(
                                                                state.cartTotals
                                                                    .total,
                                                            )
                                                        }}
                                                    </dd>
                                                </div>

                                                <div
                                                    v-if="
                                                        initialCountry.code !==
                                                        'US'
                                                    "
                                                    class="mt-2 text-right text-xs text-gray-500"
                                                >
                                                    <span
                                                        >* All prices in
                                                        USD.</span
                                                    >
                                                </div>
                                            </div>

                                            <div class="mt-4 mb-2">
                                                <div
                                                    class="relative flex items-start"
                                                >
                                                    <div
                                                        class="flex items-center h-5"
                                                    >
                                                        <input
                                                            v-model="
                                                                inputs.agree
                                                            "
                                                            id="agree"
                                                            name="agree"
                                                            type="checkbox"
                                                            :class="[
                                                                hasError(
                                                                    formErrors,
                                                                    'agree',
                                                                ) &&
                                                                !inputs.agree
                                                                    ? 'ring-2 ring-red-500 border-red-500'
                                                                    : '',
                                                                'focus:ring-transparent h-4 w-4 text-nutreance-green border-gray-300 rounded',
                                                            ]"
                                                        />
                                                    </div>
                                                    <div class="ml-3 text-sm">
                                                        <label
                                                            for="agree"
                                                            :class="[
                                                                hasError(
                                                                    formErrors,
                                                                    'agree',
                                                                ) &&
                                                                !inputs.agree
                                                                    ? 'text-red-600'
                                                                    : 'text-gray-700',
                                                            ]"
                                                        >
                                                            {{
                                                                hasError(
                                                                    formErrors,
                                                                    "agree",
                                                                ) &&
                                                                !inputs.agree
                                                                    ? "Please review and agree to the"
                                                                    : "I agree to the"
                                                            }}
                                                            <slot name="terms"
                                                        /></label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                class="order-first lg:order-last overflow-hidden bg-white shadow rounded-lg border border-gray-200 my-6 lg:my-2"
                            >
                                <CouponCodeInput :errors="errors" />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup lang="ts">
import { inject, onMounted, reactive, ref } from "vue";
import { LoginIcon } from "@heroicons/vue/outline";
import { LockClosedIcon, ViewListIcon } from "@heroicons/vue/solid";
import { useCart } from "../../functions/useCart";
import { useMoney } from "../../functions/useMoney";
import { useForm } from "../../functions/useForm";
import { useDeviceFingerprinting } from "../../functions/useDeviceFingerprinting";
import { useGoogleDataLayer } from "../../functions/useGoogleDataLayer";
import CartItemImage from "../cart/CartItemImage.vue";
import CartItemQuantityButtons from "../cart/CartItemQuantityButtons.vue";
import CartItemSubscriptionOptionButtons from "../cart/CartItemSubscriptionOptionButtons.vue";
import DeleteCartItemButton from "../cart/DeleteCartItemButton.vue";
import CartItemDiscounts from "../cart/CartItemDiscounts.vue";
import CartItemTips from "../cart/CartItemTips.vue";
import CouponCodeInput from "../cart/CouponCodeInput.vue";
import CheckoutShippingOptionButtons from "./CheckoutShippingOptionButtons.vue";
import GuestCheckoutContactInformationForm from "./GuestCheckoutContactInformationForm.vue";
import GuestCheckoutShippingAddressForm from "./GuestCheckoutShippingAddressForm.vue";
import GuestCheckoutPaymentMethodForm from "./GuestCheckoutPaymentMethodForm.vue";
import Tooltip from "../Tooltip.vue";
import LoadingIcon from "../LoadingIcon.vue";
import { DateTime } from "luxon";
import type { AppEmitter } from "../../emitter";

const props = withDefaults(
    defineProps<{
        countries?: any;
        csrf?: string;
        emailOptIn?: string;
        formErrors?: any;
        initialCart?: any;
        initialCartQuantity?: number;
        initialCartTotals?: any;
        initialDiscounts?: any;
        initialTips?: any;
        initialShippingTime?: any;
        initialShippingOptions?: any;
        initialCountry?: any;
        initialRegion?: string;
        initialStates?: any;
        old?: any;
        paymentGateway?: any;
        selectedShippingAddress?: any;
        addresses?: any;
        selectedPaymentMethod?: any;
        paymentMethods?: any;
    }>(),
    {
        emailOptIn: "1",
    },
);

const emitter = inject<AppEmitter>("mitt")!;

const { error, hasError, oldInput, submitted } = useForm();
const { dollarsForDisplay } = useMoney();
const {
    errors,
    state,
    cartIsEmpty,
    onCartItemError,
    onCartItemUpdated,
    onShippingOptionError,
    onShippingOptionUpdated,
    onShippingRegionUpdated,
    onShippingRegionError,
    onCouponApplied,
    onCouponError,
    onCouponRemoved,
    onPaypalTokenGenerated,
    onPaypalTokenError,
} = useCart();
const { fingerprint } = useDeviceFingerprinting();
const {
    pushAddShippingInformationToDataLayer,
    pushAddPaymentInformationToDataLayer,
} = useGoogleDataLayer();

const form = ref<any>(null);
const payWith = ref("card");
const inputs = reactive({
    agree:
        Object.keys(props.old).length > 0
            ? !!oldInput(props.old, "agree")
            : true,
});
const timezone = DateTime.local().toFormat("z");

Object.assign(state, {
    cart: props.initialCart,
    cartQuantity: props.initialCartQuantity,
    cartTotals: props.initialCartTotals,
    discounts: props.initialDiscounts,
    tips: props.initialTips,
    shippingTimeId: props.initialShippingTime
        ? props.initialShippingTime.id
        : null,
    shippingOptions: props.initialShippingOptions,
    country: props.initialCountry,
    region: props.initialRegion,
    paypalToken: null,
});

function onPaymentMethodEntered() {
    pushAddPaymentInformationToDataLayer({
        cartItems: state.cart.cart_items,
        total: state.cartTotals.total,
        paymentType: "credit_card",
    });
}

function onSubmit() {
    if (submitted.value) {
        return;
    }

    if (!state.region && !state.shippingTimeId) {
        submitted.value = true;
        emitter.emit("updateShippingOptions");
        return;
    }

    submitted.value = true;

    if (payWith.value === "card") {
        emitter.emit("tokenizeCardData");
        return;
    }

    form.value.submit();
}

function onCardDataTokenized() {
    form.value.submit();
}

function onCardDataTokenizationFailed() {
    submitted.value = false;
}

onMounted(() => {
    emitter.on("cartItemUpdated", (data) => onCartItemUpdated(data));
    emitter.on("cartItemError", (error) => onCartItemError(error));
    emitter.on("shippingOptionUpdated", (data) =>
        onShippingOptionUpdated(data),
    );
    emitter.on("shippingOptionError", (error) => onShippingOptionError(error));
    emitter.on("shippingRegionUpdated", (data) => {
        onShippingRegionUpdated(data);
        submitted.value = false;

        pushAddShippingInformationToDataLayer({
            cartItems: state.cart.cart_items,
            total: state.cartTotals.total,
            shippingTimeId: state.shippingTimeId,
        });
    });
    emitter.on("shippingRegionError", (error) => {
        onShippingRegionError(error);
        submitted.value = false;
    });
    emitter.on("couponApplied", (data) => onCouponApplied(data));
    emitter.on("couponError", (error) => onCouponError(error));
    emitter.on("couponRemoved", (data) => onCouponRemoved(data));
    emitter.on("paypalTokenGenerated", (data) => onPaypalTokenGenerated(data));
    emitter.on("paypalTokenError", (data) => onPaypalTokenError(data));
});
</script>
